/**
 * UserPrefsService — per-user UI preferences that must follow the person, not
 * the device.
 *
 * Anthony works across a laptop, a phone and the installed PWA, so a preference
 * kept in localStorage (the sidebar-collapse precedent) would have to be set
 * three times and would vanish on a site-data clear. This is the Firestore half:
 * one document per user, namespaced by feature, so later preferences join it
 * instead of spawning a second mechanism.
 *
 *   userPrefs/{uid} = { workBoard: { properties: [...] }, ... }
 *
 * WHY doc-id-as-uid rather than the `taskReads` query-by-uid-field shape: this
 * is a singleton per user, not a set of rows. Keying by uid makes the read a
 * document get instead of a query, and makes the rule a uid comparison instead
 * of a field comparison — strictly simpler on both sides.
 *
 * Rules (firestore.rules), following the proven taskReads self-scoping:
 *   match /userPrefs/{uid} {
 *     allow read, write: if isSignedIn() && request.auth.uid == uid;
 *   }
 */

import { db } from '../firebase';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';

const COLLECTION = 'userPrefs';

/**
 * Live-subscribe to one user's preferences.
 *
 * Calls back with `{}` and keeps going on ANY failure — a missing doc, a denied
 * read, an offline client. A preference surface is never worth taking the board
 * down for: the caller's defaults are a complete, usable answer, so the failure
 * mode is "you see the default card" rather than an error state.
 *
 * @returns {() => void} unsubscribe
 */
export const subscribeToMyPrefs = (uid, callback) => {
	if (!uid) {
		callback({});
		return () => {};
	}
	return onSnapshot(
		doc(db, COLLECTION, uid),
		(snap) => callback(snap.exists() ? snap.data() || {} : {}),
		() => callback({})
	);
};

/**
 * Merge one namespace into the user's preferences.
 *
 * `setDoc(..., { merge: true })` rather than updateDoc: the document does not
 * exist until the first preference is saved, and update would fail on a missing
 * doc. Merge is also namespace-safe — writing `workBoard` cannot clobber a
 * sibling namespace written by another surface.
 */
export const saveMyPrefs = async (uid, namespace, value) => {
	if (!uid || !namespace) return;
	await setDoc(doc(db, COLLECTION, uid), { [namespace]: value }, { merge: true });
};
